import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, MapPin, Sparkles, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import PhotoUploader from '../components/PhotoUploader';
import LocationMap from '../components/LocationMap';
import ZulassungsscheinScanner from '../components/ZulassungsscheinScanner';
import VoiceRecorder from '../components/VoiceRecorder';
import OpponentDataPanel from '../components/OpponentDataPanel';
import SeverityBadge from '../components/SeverityBadge';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { API, aiApi } from '../lib/api';

const STEPS = [
  'Persönliche Daten',
  'Fahrzeug',
  'Unfall',
  'Situation',
  'Fotos & Standort',
  'Unfallgegner',
  'Prüfen & Senden',
];

function yesNo({ name, value, onChange, testid }) {
  return (
    <RadioGroup value={value === null ? '' : value ? 'yes' : 'no'} onValueChange={(v) => onChange(v === 'yes')} className="flex gap-3" data-testid={testid}>
      <label className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer flex-1 justify-center">
        <RadioGroupItem value="yes" id={`${name}-yes`} />
        <span>Ja</span>
      </label>
      <label className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer flex-1 justify-center">
        <RadioGroupItem value="no" id={`${name}-no`} />
        <span>Nein</span>
      </label>
    </RadioGroup>
  );
}

export default function AccidentReportWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const isEmergency = location.state?.emergency || false;
  const focusPhotos = location.state?.focus === 'photos';

  const [step, setStep] = useState(focusPhotos ? 4 : 0);
  const [submitting, setSubmitting] = useState(false);
  const [coords, setCoords] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [opponent, setOpponent] = useState({});
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [previewAnalysis, setPreviewAnalysis] = useState(null);

  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    make: '', model: '', license_plate: '', vin: '', first_registration: '', power_kw: '', fuel_type: '',
    date: '', time: '', description: '',
    drivable: null, police_present: null, other_party: null, personal_injury: null,
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Auto-set today's date once at mount if empty
  useEffect(() => {
    if (!form.date) {
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      setForm((p) => ({ ...p, date: today, time: `${hh}:${mm}` }));
    }
    // eslint-disable-next-line
  }, []);

  // Skip opponent step when no opponent involved
  const visibleSteps = STEPS.map((label, i) => ({ label, idx: i })).filter(s => !(s.idx === 5 && form.other_party === false));

  const validateStep = () => {
    if (step === 0) {
      if (!form.first_name || !form.last_name || !form.phone || !form.email) return 'Bitte alle Felder ausfüllen.';
      if (!/^\S+@\S+\.\S+$/.test(form.email)) return 'Bitte gültige E-Mail eingeben.';
    }
    if (step === 1) {
      if (!form.make || !form.model || !form.license_plate) return 'Bitte Fahrzeugdaten vollständig.';
    }
    if (step === 2) {
      if (!form.date || !form.time) return 'Bitte Datum und Uhrzeit angeben.';
      if (!form.description || form.description.length < 10) return 'Bitte kurze Beschreibung (mind. 10 Zeichen).';
    }
    if (step === 3) {
      const flags = ['drivable', 'police_present', 'other_party', 'personal_injury'];
      for (const k of flags) {
        if (form[k] === null) return 'Bitte alle Fragen beantworten.';
      }
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    // Skip opponent step if no opponent
    let nextIdx = Math.min(STEPS.length - 1, step + 1);
    if (nextIdx === 5 && form.other_party === false) nextIdx = 6;
    setStep(nextIdx);
  };
  const prev = () => {
    let prevIdx = Math.max(0, step - 1);
    if (prevIdx === 5 && form.other_party === false) prevIdx = 4;
    setStep(prevIdx);
  };

  const requestLocation = () => {
    if (!navigator.geolocation) return toast.error('GPS nicht verfügbar');
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }); toast.success('Standort erfasst'); },
      () => toast.error('Standort konnte nicht ermittelt werden'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const applyVehicleOCR = (res) => {
    if (!res) return;
    setForm((p) => ({
      ...p,
      license_plate: res.license_plate ? String(res.license_plate).toUpperCase() : p.license_plate,
      make: res.make || p.make,
      model: res.model || p.model,
      vin: res.vin || p.vin,
      first_registration: res.first_registration || p.first_registration,
      power_kw: res.power_kw != null ? String(res.power_kw) : p.power_kw,
      fuel_type: res.fuel_type || p.fuel_type,
    }));
  };

  const runPreviewAnalysis = async (uploadedPhotoIds) => {
    if (!uploadedPhotoIds || uploadedPhotoIds.length === 0) return;
    setAnalyzing(true);
    try {
      // We need a case_id, so this runs after submit. Use placeholder analysis only.
      // (Real analysis is triggered post-submit; here we only display photos.)
    } finally {
      setAnalyzing(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        person: { first_name: form.first_name, last_name: form.last_name, phone: form.phone, email: form.email },
        vehicle: {
          make: form.make,
          model: form.model,
          license_plate: form.license_plate,
          vin: form.vin || null,
          first_registration: form.first_registration || null,
          power_kw: form.power_kw ? Number(form.power_kw) : null,
          fuel_type: form.fuel_type || null,
        },
        accident: { date: form.date, time: form.time, description: form.description },
        situation: {
          drivable: !!form.drivable,
          police_present: !!form.police_present,
          other_party: !!form.other_party,
          personal_injury: !!form.personal_injury,
        },
        location: coords,
        is_emergency: isEmergency,
        photos: [],
        opponent: form.other_party ? opponent : null,
        voice_transcript: voiceTranscript || null,
      };
      const { data: caseData } = await API.post('/cases', payload);

      // Upload photos sequentially
      for (const p of photos) {
        const base64 = (p.dataUrl || '').split(',').pop();
        if (!base64) continue;
        try {
          await API.post('/files/upload', {
            case_id: caseData.id,
            filename: p.filename || 'photo.jpg',
            mime_type: p.mime_type || 'image/jpeg',
            data_base64: base64,
            category: 'photo',
          });
        } catch (e) {
          console.warn('Photo upload failed', e);
        }
      }

      // Kick off AI analysis + admin summary in background (don't wait)
      try {
        if (photos.length > 0) {
          aiApi.analyze(caseData.id).catch(() => {});
          // Admin summary runs after analyze internally if needed
          setTimeout(() => aiApi.adminSummary(caseData.id).catch(() => {}), 5000);
        }
      } catch (e) { /* ignore */ }

      toast.success('Schadenfall erfolgreich gemeldet');
      navigate('/track', { state: { case_number: caseData.case_number, pin: caseData.tracking_pin, case_id: caseData.id } });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Fehler beim Erstellen des Falls');
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = ((step + 1) / STEPS.length) * 100;
  const isLastStep = step === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-32">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-heading font-semibold text-2xl sm:text-3xl text-[hsl(var(--primary))]">Unfall melden</h1>
            <p className="text-sm text-slate-600">Schritt {step + 1} von {STEPS.length} — {STEPS[step]}</p>
          </div>
          <div className="text-xs text-slate-500">KI hilft beim Ausfüllen</div>
        </div>
        <Progress value={progressPct} className="h-2 mb-6" data-testid="accident-wizard-progress" />

        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-5 sm:p-6 space-y-4">
            {step === 0 && (
              <div className="space-y-4" data-testid="wizard-step-1">
                <div>
                  <Label>Vorname</Label>
                  <Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className="h-12 rounded-xl mt-1" data-testid="accident-form-first-name-input" />
                </div>
                <div>
                  <Label>Nachname</Label>
                  <Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className="h-12 rounded-xl mt-1" data-testid="accident-form-last-name-input" />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+43 660 ..." className="h-12 rounded-xl mt-1" data-testid="accident-form-phone-input" />
                </div>
                <div>
                  <Label>E-Mail</Label>
                  <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="h-12 rounded-xl mt-1" data-testid="accident-form-email-input" />
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="space-y-4" data-testid="wizard-step-2">
                <ZulassungsscheinScanner onExtracted={applyVehicleOCR} />
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label>Kennzeichen</Label>
                    <Input value={form.license_plate} onChange={(e) => set('license_plate', e.target.value.toUpperCase())} placeholder="z.B. W-1234AB" className="h-12 rounded-xl mt-1 uppercase" data-testid="accident-form-license-plate-input" />
                  </div>
                  <div>
                    <Label>Marke</Label>
                    <Input value={form.make} onChange={(e) => set('make', e.target.value)} placeholder="z.B. BMW" className="h-12 rounded-xl mt-1" data-testid="accident-form-make-input" />
                  </div>
                  <div>
                    <Label>Modell</Label>
                    <Input value={form.model} onChange={(e) => set('model', e.target.value)} placeholder="z.B. 320d" className="h-12 rounded-xl mt-1" data-testid="accident-form-model-input" />
                  </div>
                  <div>
                    <Label>FIN/VIN (optional)</Label>
                    <Input value={form.vin} onChange={(e) => set('vin', e.target.value.toUpperCase())} placeholder="17-stellig" className="h-12 rounded-xl mt-1" data-testid="accident-form-vin-input" />
                  </div>
                  <div>
                    <Label>Erstzulassung (optional)</Label>
                    <Input type="date" value={form.first_registration} onChange={(e) => set('first_registration', e.target.value)} className="h-12 rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label>Leistung kW (optional)</Label>
                    <Input type="number" value={form.power_kw} onChange={(e) => set('power_kw', e.target.value)} className="h-12 rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label>Kraftstoff (optional)</Label>
                    <Input value={form.fuel_type} onChange={(e) => set('fuel_type', e.target.value)} placeholder="Benzin / Diesel / Elektro" className="h-12 rounded-xl mt-1" />
                  </div>
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-4" data-testid="wizard-step-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Datum</Label>
                    <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="h-12 rounded-xl mt-1" data-testid="accident-form-date-input" />
                  </div>
                  <div>
                    <Label>Uhrzeit</Label>
                    <Input type="time" value={form.time} onChange={(e) => set('time', e.target.value)} className="h-12 rounded-xl mt-1" data-testid="accident-form-time-input" />
                  </div>
                </div>
                <VoiceRecorder
                  vehicle={{ make: form.make, model: form.model, license_plate: form.license_plate }}
                  accidentDate={form.date}
                  accidentTime={form.time}
                  onStructured={(text, raw) => { set('description', text); setVoiceTranscript(raw || ''); }}
                />
                <div>
                  <Label>Unfallbeschreibung</Label>
                  <Textarea rows={5} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Sprechen oder tippen – die KI strukturiert es für Sie." className="rounded-xl mt-1" data-testid="accident-form-description-textarea" />
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-5" data-testid="wizard-step-4">
                <div>
                  <Label className="mb-2 block">Ist Ihr Fahrzeug fahrbereit?</Label>
                  {yesNo({ name: 'drivable', value: form.drivable, onChange: (v) => set('drivable', v), testid: 'accident-form-drivable-radio' })}
                  {form.drivable === false && (
                    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="not-drivable-notice">
                      <strong>Hinweis:</strong> Aktuell ist kein eigener Abschleppservice aktiviert. Bitte kontaktieren Sie uns telefonisch oder per WhatsApp – wir helfen Ihnen bei der weiteren Koordination der nächsten Schritte.
                    </div>
                  )}
                </div>
                <div>
                  <Label className="mb-2 block">War die Polizei vor Ort?</Label>
                  {yesNo({ name: 'police', value: form.police_present, onChange: (v) => set('police_present', v), testid: 'accident-form-police-radio' })}
                </div>
                <div>
                  <Label className="mb-2 block">Gab es einen Unfallgegner?</Label>
                  {yesNo({ name: 'opponent', value: form.other_party, onChange: (v) => set('other_party', v), testid: 'accident-form-opponent-radio' })}
                </div>
                <div>
                  <Label className="mb-2 block">Gab es Personenschäden?</Label>
                  {yesNo({ name: 'injury', value: form.personal_injury, onChange: (v) => set('personal_injury', v), testid: 'accident-form-injury-radio' })}
                </div>
              </div>
            )}
            {step === 4 && (
              <div className="space-y-5" data-testid="wizard-step-5">
                <div>
                  <Label className="mb-2 block">Unfallfotos</Label>
                  <PhotoUploader value={photos} onChange={setPhotos} max={8} />
                  <p className="text-xs text-slate-500 mt-2">Die KI analysiert die Fotos nach dem Absenden automatisch.</p>
                </div>

                {/* Promise / motivation card: show the user the destination once they upload their first photo. */}
                {photos.length > 0 && (
                  <div
                    className="rounded-2xl border border-[hsl(var(--accent))]/20 bg-gradient-to-br from-sky-50/70 to-white p-4 sm:p-5 uh-shadow-sm"
                    data-testid="wizard-before-after-promise"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white border border-sky-200 text-[hsl(var(--primary))]">
                        <Sparkles className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <div className="font-heading font-semibold text-[hsl(var(--primary))] text-base leading-tight">
                          So machen wir das wieder ganz.
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          Wir kümmern uns um Gutachter, Versicherung und Werkstatt &mdash; damit Ihr Fahrzeug
                          schnell und ohne Stress wieder einsatzbereit ist.
                        </p>
                      </div>
                    </div>
                    <BeforeAfterSlider
                      compact
                      caption="Veranschaulichung &mdash; Ihr individuelles Ergebnis hängt von Schadenart, Versicherung und Werkstatt ab."
                    />
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Unfallort (GPS)</Label>
                    <Button type="button" variant="outline" size="sm" onClick={requestLocation} data-testid="accident-form-gps-button">
                      <MapPin className="h-4 w-4 mr-1.5" /> Standort erfassen
                    </Button>
                  </div>
                  {coords ? (
                    <>
                      <LocationMap lat={coords.lat} lng={coords.lng} height={220} testid="accident-form-map" />
                      <p className="text-xs text-slate-500 mt-2">{coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}</p>
                    </>
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">Standort optional &mdash; hilft uns bei der schnelleren Bearbeitung.</div>
                  )}
                </div>
              </div>
            )}
            {step === 5 && (
              <div className="space-y-3" data-testid="wizard-step-6">
                <p className="text-sm text-slate-600">Daten des Unfallgegners. Foto vom Führerschein, Zulassungsschein oder der Versicherungskarte machen — die KI übernimmt die Daten automatisch.</p>
                <OpponentDataPanel value={opponent} onChange={setOpponent} />
              </div>
            )}
            {step === 6 && (
              <div className="space-y-4" data-testid="wizard-step-7">
                <div>
                  <div className="font-heading font-semibold text-slate-900">Bereit zum Absenden</div>
                  <p className="text-sm text-slate-600">Nach dem Absenden erhalten Sie eine Fallnummer und eine 6-stellige PIN. Die KI startet automatisch die Schadensanalyse.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-slate-50 p-3 border border-slate-200">
                    <div className="text-xs text-slate-500">Kunde</div>
                    <div className="font-medium">{form.first_name} {form.last_name}</div>
                    <div className="text-slate-600">{form.phone} • {form.email}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3 border border-slate-200">
                    <div className="text-xs text-slate-500">Fahrzeug</div>
                    <div className="font-medium">{form.make} {form.model}</div>
                    <div className="text-slate-600">{form.license_plate}{form.vin ? ` • ${form.vin}` : ''}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3 border border-slate-200">
                    <div className="text-xs text-slate-500">Unfall</div>
                    <div className="font-medium">{form.date} {form.time}</div>
                    <div className="text-slate-600 line-clamp-2">{form.description}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3 border border-slate-200">
                    <div className="text-xs text-slate-500">Anhaenge</div>
                    <div className="font-medium">{photos.length} Foto(s)</div>
                    <div className="text-slate-600">{coords ? `Standort: ${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}` : 'Kein Standort'}</div>
                  </div>
                  {form.other_party && (
                    <div className="rounded-xl bg-slate-50 p-3 border border-slate-200 sm:col-span-2">
                      <div className="text-xs text-slate-500">Unfallgegner</div>
                      <div className="font-medium">{opponent.name || '— nicht erfasst —'}</div>
                      <div className="text-slate-600">{opponent.license_plate} {opponent.insurance_company && `• ${opponent.insurance_company}`}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 uh-safe-bottom">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Button variant="outline" onClick={prev} disabled={step === 0} data-testid="wizard-back-button">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Zurück
          </Button>
          {!isLastStep ? (
            <Button onClick={next} className="bg-[hsl(var(--primary))] hover:bg-[hsl(210_73%_12%)] text-white" data-testid="wizard-next-button">
              Weiter <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting} className="bg-[hsl(var(--accent))] hover:bg-[hsl(217_90%_48%)] text-white" data-testid="wizard-submit-button">
              {submitting ? 'Sende...' : (<>Fall melden <Check className="h-4 w-4 ml-1.5" /></>)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
